// identity.ts — ΜΙΑ πηγή αλήθειας ανά άτομο.
// (α) Μεταγραφή Ελληνικά→Λατινικά (ΑΑΔΕ/Crew List/DOCX θέλουν λατινικά), idempotent.
// (β) normId: κλειδί ταιριάσματος από ID/passport (χωρίς κενά/τόνους/case, GR→EN).
// (γ) sameId / sameIdentity: ταίριασμα με ID (primary) + fallback DOB+gender.
// (δ) canonPerson / mergePerson / unifyCrew: ένα record ανά άτομο, fill-empty-only, ΠΟΤΕ διπλό.

// --- Πίνακας μεταγραφής -------------------------------------------------------
// Δίψηφα (ελέγχονται ΠΡΩΤΑ — longest match):
const DIGRAPHS: Record<string, string> = {
  'ου': 'ou', 'αυ': 'av', 'ευ': 'ev', 'ηυ': 'iv',
  'μπ': 'b', 'ντ': 'd', 'γγ': 'ng', 'γκ': 'g', 'γξ': 'nx', 'γχ': 'nch',
  'τσ': 'ts', 'τζ': 'tz', 'αι': 'ai', 'ει': 'ei', 'οι': 'oi', 'υι': 'yi',
};
// Μονά (ELOT 743):
const SINGLE: Record<string, string> = {
  'α': 'a', 'β': 'v', 'γ': 'g', 'δ': 'd', 'ε': 'e', 'ζ': 'z', 'η': 'i', 'θ': 'th',
  'ι': 'i', 'κ': 'k', 'λ': 'l', 'μ': 'm', 'ν': 'n', 'ξ': 'x', 'ο': 'o', 'π': 'p',
  'ρ': 'r', 'σ': 's', 'ς': 's', 'τ': 't', 'υ': 'y', 'φ': 'f', 'χ': 'ch', 'ψ': 'ps', 'ω': 'o',
  // τονισμένα:
  'ά': 'a', 'έ': 'e', 'ή': 'i', 'ί': 'i', 'ό': 'o', 'ύ': 'y', 'ώ': 'o',
  'ϊ': 'i', 'ϋ': 'y', 'ΐ': 'i', 'ΰ': 'y',
};

const GREEK_RE = /[Ͱ-Ͽἀ-῿]/;
const hasGreek = (s: string): boolean => GREEK_RE.test(s);

// Μεταγράφει ΕΝΑ token (λέξη). Διατηρεί case σε επίπεδο λέξης:
// ΟΛΑ ΚΕΦΑΛΑΙΑ→UPPER, Τιτλος→Title, αλλιώς lower. Idempotent για ήδη-λατινικά.
function transliterateWord(word: string): string {
  if (!hasGreek(word)) return word; // ήδη λατινικά/αριθμοί → μη το πειράξεις
  const w = word.toLowerCase();
  let out = '';
  let i = 0;
  while (i < w.length) {
    const two = w.substr(i, 2);
    if (DIGRAPHS[two]) { out += DIGRAPHS[two]; i += 2; continue; }
    const one = w[i];
    out += (SINGLE[one] !== undefined ? SINGLE[one] : one);
    i += 1;
  }
  const greekLetters = Array.from(word).filter((c) => GREEK_RE.test(c));
  const allUpper = greekLetters.length > 0 && greekLetters.every((c) => c === c.toUpperCase());
  const firstUpper = GREEK_RE.test(word[0]) ? word[0] === word[0].toUpperCase() : false;
  if (allUpper) return out.toUpperCase();
  if (firstUpper) return out.charAt(0).toUpperCase() + out.slice(1);
  return out;
}

// Μεταγράφει ολόκληρο string (διατηρεί κενά/σημεία στίξης). Idempotent.
export function toLatin(s: any): string {
  if (s == null) return '';
  const str = String(s);
  if (!hasGreek(str)) return str; // idempotent: ό,τι είναι ήδη λατινικά ΔΕΝ αλλάζει
  return str.split(/(\s+)/).map((tok) => (/\s+/.test(tok) ? tok : transliterateWord(tok))).join('');
}

// --- ID/passport: ΟΠΤΙΚΑ ΟΜΟΓΛΥΦΑ (όχι φωνητικά!) ------------------------------
// Πίνακας ταυτότητας (εντολή Kostas): οπτικά ομόγλυφα ΕΚΤΟΣ από Ρ→R (φωνητικό, ρητή επιλογή).
// Η→H, Β→B, Χ→X, Υ→Y (ομόγλυφα) · Ρ→R. Έτσι "ΑΡ 009943" → "AR 009943". ΔΙΑΦΟΡΕΤΙΚΟ από τα ονόματα.
const ID_HOMOGLYPH: Record<string, string> = {
  'Α': 'A', 'Β': 'B', 'Ε': 'E', 'Ζ': 'Z', 'Η': 'H', 'Ι': 'I', 'Κ': 'K',
  'Μ': 'M', 'Ν': 'N', 'Ο': 'O', 'Ρ': 'R', 'Τ': 'T', 'Υ': 'Y', 'Χ': 'X',
};
// Μεταγραφή ID/passport με ομόγλυφα. Idempotent (λατινικά/αριθμοί περνούν αμετάβλητα).
export function latinId(s: any): string {
  if (s == null) return '';
  return String(s).split('').map((ch) => {
    const up = ch.toUpperCase();
    const map = ID_HOMOGLYPH[up];
    if (!map) return ch; // αριθμός/σύμβολο/ήδη λατινικό → ως έχει
    return ch === up ? map : map.toLowerCase();
  }).join('');
}

// Κλειδί ταιριάσματος: ΟΜΟΓΛΥΦΑ + UPPER + μόνο A-Z0-9. "ΑΡ 009943" → "AP009943".
export function normId(v: any): string {
  return latinId(String(v == null ? '' : v)).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

const idOf = (m: any): string =>
  normId(m?.passport || m?.idNumber || m?.skipperIdNumber || m?.skipperPassport || '');
const dobOf = (m: any): string => String(m?.dateOfBirth || m?.dob || m?.skipperDob || '').trim();
const genOf = (m: any): string => String(m?.gender || m?.skipperGen || m?.skipperGender || '').trim().toLowerCase();

// Αυστηρό ταίριασμα ΜΟΝΟ με ID (και τα δύο non-empty). Για dedup crew (αποφυγή false merge).
export function sameId(a: any, b: any): boolean {
  const ia = idOf(a), ib = idOf(b);
  return !!ia && !!ib && ia === ib;
}

// Ταίριασμα skipper: ID (primary) → fallback DOB+gender ΜΟΝΟ όταν λείπει ID σε μία πλευρά.
export function sameIdentity(a: any, b: any): boolean {
  const ia = idOf(a), ib = idOf(b);
  if (ia && ib) return ia === ib;
  const da = dobOf(a), db = dobOf(b), ga = genOf(a), gb = genOf(b);
  if (da && db && ga && gb) return da === db && ga === gb;
  return false;
}

// --- ΤΑΥΤΟΠΡΟΣΩΠΙΑ: ΟΝΟΜΑ **ΚΑΙ** ΤΑΥΤΟΤΗΤΑ -----------------------------------
// Κλειδί ονόματος = ΣΥΝΟΛΟ ΛΕΞΕΩΝ: λατινικά (ΠΑΤΡΙΚ==PATRICK) + κεφαλαία + στίξη έξω,
// μετά οι ΛΕΞΕΙΣ χωρίς σειρά και χωρίς επαναλήψεις (unique + sort).
//   'Patrick  Stas' == 'PATRICK STAS' == 'patrick stas'      (όπως πριν)
//   'Juan Codina Navarro Codina Navarro' == 'CODINA NAVARRO JUAN'   ← ναύλος 792:
//   το ΙΔΙΟ όνομα γραμμένο ΕΠΩΝΥΜΟ-ΟΝΟΜΑ στο Crew List και ΟΝΟΜΑ-ΕΠΩΝΥΜΟ στη φόρμα
//   του ναυλωτή (με το επώνυμο διπλογραμμένο στο firstName).
// ΠΡΟΣΟΧΗ: το όνομα ΜΟΝΟ ΤΟΥ δεν ενώνει ποτέ δύο ανθρώπους — η ταυτοπροσωπία περνά
// ΠΑΝΤΑ από το samePersonStrict, που απαιτεί ΚΑΙ κοινή ταυτότητα όταν έχουν και οι δύο.
export function normName(...parts: any[]): string {
  const joined = parts.map((p) => String(p == null ? '' : p)).join(' ');
  const words = toLatin(joined).toUpperCase().replace(/[^A-Z0-9\u0370-\u03FF]+/g, ' ').split(' ');
  const uniq: string[] = [];
  for (const w of words) { if (w !== '' && uniq.indexOf(w) === -1) uniq.push(w); }
  return uniq.sort().join('|');
}

// ΣΥΝΟΛΟ ταυτοτήτων ενός ανθρώπου. Ο ναυλωτής μπορεί να έχει ID Card και ο σκίπερ
// Passport (ή ανάποδα) — γι' αυτό μαζεύουμε ΟΛΑ τα συμπληρωμένα πεδία και μετά
// ψάχνουμε ΤΟΜΗ, όχι ίδιο-πεδίο-με-ίδιο-πεδίο. Κάθε τιμή περνά από normId:
// ομόγλυφα GR→EN, κεφαλαία, μόνο A-Z0-9 → '592-9464660-63' == '592 946466063'.
export function idKeys(...vals: any[]): string[] {
  const out: string[] = [];
  for (const v of vals) {
    const k = normId(v);
    if (k !== '' && out.indexOf(k) === -1) out.push(k);
  }
  return out;
}

// ΕΝΑΣ ΑΝΘΡΩΠΟΣ. Το όνομα πρέπει ΠΑΝΤΑ να ταιριάζει. Για την ταυτότητα:
//   • έχουν ΚΑΙ ΟΙ ΔΥΟ ID → πρέπει να έχουν ΚΟΙΝΟ. Διαφορετικά ID = ΔΥΟ ΑΝΘΡΩΠΟΙ,
//     ΠΟΤΕ ένωση (δύο συνονόματοι με άλλο διαβατήριο δεν είναι ο ίδιος).
//   • η ΜΙΑ πλευρά ΔΕΝ έχει ΚΑΜΙΑ ταυτότητα → δεν υπάρχει αντίφαση να λυθεί, οπότε
//     το ίδιο ονοματεπώνυμο αρκεί. Τυπική περίπτωση: ο ναυλωτής δεν έχει καταχωρήσει
//     ID (ναύλος 698: ναυλωτής «PATRICK STAS» χωρίς ID, σκίπερ «Patrick Stas» με
//     592-9464660-63) — ίδιος άνθρωπος, και πρέπει να μετρηθεί ΜΙΑ φορά.
export function samePersonStrict(nameA: string, idsA: string[], nameB: string, idsB: string[]): boolean {
  if (!nameA || !nameB || nameA !== nameB) return false;
  if (idsA.length && idsB.length) return idsA.some((a) => idsB.indexOf(a) !== -1);
  return true;
}

const CREW_KEYS = ['name', 'firstName', 'lastName', 'passport', 'dateOfBirth', 'gender', 'nationality', 'email', 'phone', 'address', 'taxNumber', 'taxOffice', 'role'];

// Canonicalize: όνομα/ID/passport ΠΑΝΤΑ λατινικά (idempotent). Κρατά όλα τα άλλα flags (isCharterer κλπ).
export function canonPerson(m: any): any {
  if (!m || typeof m !== 'object') return m;
  const out: any = { ...m };
  out.firstName = toLatin(out.firstName || '');   // ονόματα: φωνητικά (Η→I)
  out.lastName = toLatin(out.lastName || '');
  out.passport = latinId(out.passport || '');     // ID: ομόγλυφα (Ρ→P, Η→H)
  out.name = toLatin(out.name || '') || `${out.firstName} ${out.lastName}`.trim();
  return out;
}

// Merge δύο εκδοχών ΙΔΙΟΥ ατόμου: fill-empty-only ΑΜΦΙΔΡΟΜΟ (καμία τιμή δεν σβήνει), μετά canon.
export function mergePerson(a: any, b: any): any {
  const out: any = { ...a };
  for (const k of CREW_KEYS) {
    const av = String(a?.[k] ?? '').trim();
    const bv = String(b?.[k] ?? '').trim();
    out[k] = av !== '' ? a[k] : (bv !== '' ? b[k] : (a?.[k] ?? b?.[k] ?? ''));
  }
  // κράτα role='skipper' αν κάποιο από τα δύο είναι skipper
  if (String(a?.role || '').toLowerCase() === 'skipper' || String(b?.role || '').toLowerCase() === 'skipper') out.role = 'skipper';
  return canonPerson(out);
}

// Ενοποίηση crew: canon + merge όσων έχουν ΙΔΙΟ ID (sameId). ΕΝΑ record ανά άτομο, ΠΟΤΕ διπλό.
// Χρησιμοποιεί sameId (αυστηρό, ID-only) ώστε να ΜΗΝ ενώσει κατά λάθος διαφορετικά άτομα.
export function unifyCrew(crew: any[]): any[] {
  if (!Array.isArray(crew)) return crew;
  const out: any[] = [];
  for (const raw of crew) {
    const m = canonPerson(raw);
    const idx = out.findIndex((o) => sameId(o, m));
    if (idx >= 0) out[idx] = mergePerson(out[idx], m);
    else out.push(m);
  }
  return out;
}
